import { ReactNode } from 'react'
import { useT } from './i18n'
import { Resource } from './types'

type Props<D> = {
  title: string
  subtitle?: string
  state: Resource<D>
  /** Return a message to show instead of the content (e.g. "no data in this period"). */
  empty?: (data: D) => string | null
  render: (data: D) => ReactNode
  minHeight?: number
}

/** Card with a title and the loading / error / empty states every dashboard section shares. */
export function CardShell<D>({ title, subtitle, state, empty, render, minHeight = 260 }: Props<D>) {
  const t = useT()
  let body: ReactNode
  if (state.status === 'ready') {
    const message = empty ? empty(state.data) : null
    body = message ? (
      <div className='text-muted text-center py-10'>{message}</div>
    ) : (
      render(state.data)
    )
  } else if (state.status === 'error') {
    body = (
      <div className='alert alert-danger mb-0' role='alert'>
        {state.message}
      </div>
    )
  } else {
    body = (
      <div
        className='d-flex align-items-center justify-content-center text-muted'
        style={{ minHeight }}
        aria-busy='true'
      >
        {state.status === 'loading' ? (
          <>
            <span className='spinner-border spinner-border-sm me-3' role='status' />
            {t('PROFILES_DASHBOARD.LOADING')}
          </>
        ) : null}
      </div>
    )
  }
  return (
    <section className='card border-0 shadow-sm h-100' aria-label={title}>
      <div className='card-header border-0 pt-6'>
        <h3 className='card-title d-flex flex-column align-items-start'>
          <span className='fw-bold fs-4'>{title}</span>
          {subtitle ? <span className='text-muted fs-8 fw-normal mt-1'>{subtitle}</span> : null}
        </h3>
      </div>
      <div className='card-body pt-0' style={{ minWidth: 0 }}>
        {body}
      </div>
    </section>
  )
}
